import { useEffect, useRef, type ReactNode } from "react";

import styles from "./kit.module.css";

/**
 * A small panel anchored under whatever opened it.
 *
 * Closes on Escape and on a click outside, because a panel that traps you is
 * the fastest way to make a keyboard-driven screen feel slow. The trigger stays
 * the caller's own button — this only owns the panel.
 */
export function Popover({
  open,
  onClose,
  trigger,
  align = "left",
  wide = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  align?: "left" | "right";
  wide?: boolean;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKey);
    // Capture: a click that closes the panel should not also activate whatever
    // it landed on.
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [open, onClose]);

  return (
    <div className={styles.pop} ref={box}>
      {trigger}
      {open && (
        <div
          className={[
            styles.popPanel,
            wide ? styles.popWide : "",
            align === "right" ? styles.popRight : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** A row in a popover menu. */
export function MenuItem({
  label,
  kind,
  onClick,
  disabled,
}: {
  label: string;
  kind?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className={styles.menuItem} onClick={onClick} disabled={disabled}>
      {label}
      {kind && <span className={styles.menuKind}>{kind}</span>}
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className={styles.menuLabel}>{children}</div>;
}
